import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

import {
  LoadingSpinner,
  StationCellProps,
  StationCellType,
} from '../../components/Molecules';
import {
  GameTypeHelpPopup,
  GameTypeHelpType,
  stringToGameTypeHelpType,
} from '../../components/Popups';
import { AIRevisionGamesListTemplate } from '../../components/Templates';
import { AIRevisionStatus } from '../../constants/MemoryGames/aiRevision';
import { AppToastInfoOption } from '../../constants/toast';
import { usePopup } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { RevisionSchedule } from '../../models/RecordStore';
import { useGameStore, useGameStoreListField } from '../../stores/GameStore';
import {
  useRecordStore,
  useRecordStoreListField,
} from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { getTimeString } from '../../utils/date';
import { computeReward } from '../../utils/record';

const { fetchSpace, fetchAlbumsByIds } = useSpaceStore.getState();
const { fetchGameRecordsByGameIds } = useRecordStore.getState();
const { fetchGameCollectionsByIds, fetchGameAndStatisticsByIds } =
  useGameStore.getState();

type State = {
  loadingTotal: number;
  aiRevisionStatus: AIRevisionStatus | undefined;
  isNavigateHome: boolean;
  gameSetIds: string[] | undefined;
  gameCollectionIds: string[] | undefined;
  gameIds: string[] | undefined;
  gameIdDetails:
    | {
        gameCollectionId: string;
        gameId: string;
        hash: string;
      }[]
    | undefined;
  finishInitSetting: boolean; // for check the revision schedule turn to the ids need to fetch
};

const AIMemoryBoostGameListScreen: React.FC = () => {
  const navigate = useNavigate();
  const { classroomId, gameSetId, aiRevisionStatus } = useParams();
  const { showPopup, hidePopup } = usePopup();
  const { t } = useTranslation();
  const userId = useSessionStore((state) => state.activeUserId);

  const userProfile = useUserStore((store) => store.getProfile(userId ?? ''));

  useRedirectEntry();

  const aiRevisionStatusEnum =
    AIRevisionStatus[aiRevisionStatus as keyof typeof AIRevisionStatus];

  useEffect(() => {
    if (aiRevisionStatusEnum) {
      switch (aiRevisionStatusEnum) {
        case AIRevisionStatus.Ready: {
          setState((s) => ({ ...s, aiRevisionStatus: AIRevisionStatus.Ready }));
          break;
        }
        case AIRevisionStatus.Scheduled: {
          setState((s) => ({
            ...s,
            aiRevisionStatus: AIRevisionStatus.Scheduled,
          }));
          break;
        }
        case AIRevisionStatus.Completed: {
          setState((s) => ({
            ...s,
            aiRevisionStatus: AIRevisionStatus.Completed,
          }));
          break;
        }
        default: {
          setState((s) => ({ ...s, isNavigateHome: true }));
        }
      }
    } else {
      setState((s) => ({ ...s, isNavigateHome: true }));
    }
  }, [aiRevisionStatusEnum]);

  const [state, setState] = useState<State>({
    loadingTotal: 0,
    aiRevisionStatus: undefined,
    isNavigateHome: false,
    gameSetIds: undefined,
    gameCollectionIds: undefined,
    gameIds: undefined,
    gameIdDetails: undefined,
    finishInitSetting: false,
  });

  useRedirectHome(state.isNavigateHome);

  const spaceFieldName = `${aiRevisionStatus}_${classroomId}_memory_boost`;
  useEffect(() => {
    if (classroomId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      fetchSpace(spaceFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [classroomId, spaceFieldName]);

  const albumFieldName = `${aiRevisionStatus}_${classroomId}_${gameSetId}_memory_boost`;
  useSpaceStoreListField('albums', albumFieldName);

  const fetchAlbums = useCallback(async () => {
    if (state.gameSetIds && classroomId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      await fetchAlbumsByIds({
        fieldName: albumFieldName,
        classroomId: classroomId,
        gameSetIds: state.gameSetIds,
      });
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    }
  }, [albumFieldName, classroomId, state.gameSetIds]);

  useEffect(() => {
    fetchAlbums().catch(console.error);
  }, [fetchAlbums]);

  const gameCollectionFieldName = `${aiRevisionStatus}_${classroomId}_${gameSetId}_memory_boost`;
  useGameStoreListField('gameCollections', gameCollectionFieldName);

  const fetchGameCollections = useCallback(async () => {
    if (state.gameCollectionIds && classroomId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      await fetchGameCollectionsByIds({
        fieldName: gameCollectionFieldName,
        gameCollectionIds: state.gameCollectionIds,
      });
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    }
  }, [state.gameCollectionIds, classroomId, gameCollectionFieldName]);

  useEffect(() => {
    fetchGameCollections().catch(console.error);
  }, [fetchGameCollections]);

  const gameFieldName = `${aiRevisionStatus}_${classroomId}_${gameSetId}_memory_boost`;
  useGameStoreListField('games', gameFieldName);
  useGameStoreListField('gameStatistics', gameFieldName);

  const fetchGames = useCallback(async () => {
    if (state.gameIdDetails && classroomId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      await fetchGameAndStatisticsByIds({
        fieldName: gameFieldName,
        gameIds: state.gameIdDetails,
      });
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    }
  }, [state.gameIdDetails, classroomId, gameFieldName]);

  useEffect(() => {
    fetchGames().catch(console.error);
  }, [fetchGames]);

  const games = useGameStore((store) => {
    return state.gameIds?.map((gameId) => store.getGame(gameId));
  }, arrayEqual(shallow));

  const gameRecordFieldName = `${aiRevisionStatus}_${classroomId}_${gameSetId}_memory_boost`;
  useRecordStoreListField('gameRecords', gameRecordFieldName);

  const fetchGameRecords = useCallback(async () => {
    if (state.gameIds && classroomId && userId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      await fetchGameRecordsByGameIds({
        fieldName: gameRecordFieldName,
        classroomId: classroomId,
        playedBy: userId,
        gameIds: state.gameIds,
      });
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
    }
  }, [state.gameIds, classroomId, userId, gameRecordFieldName]);

  useEffect(() => {
    fetchGameRecords().catch(console.error);
  }, [fetchGameRecords]);

  const revisionScheduleFieldName = `${state.aiRevisionStatus}_${userId}_${classroomId}_${gameSetId}`;
  const revisionScheduleField = useRecordStoreListField(
    'revisionSchedules',
    revisionScheduleFieldName,
  );

  const fetchRevisionSchedules = useCallback<FetchListFn>(
    (cursor) =>
      useRecordStore.getState().fetchRevisionSchedulesByType(
        {
          revisionType: state.aiRevisionStatus ?? AIRevisionStatus.Ready,
          classroomId: classroomId ?? '',
          gameSetId,
          userId: userId ?? '',
        },
        cursor,
      ),
    [classroomId, gameSetId, state.aiRevisionStatus, userId],
  );

  const { refresh: refreshRevisionSchedules } = useFetchList(
    revisionScheduleField,
    fetchRevisionSchedules,
  );

  useEffect(() => {
    if (classroomId && state.aiRevisionStatus) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      refreshRevisionSchedules()
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
        });
    }
  }, [classroomId, refreshRevisionSchedules, state.aiRevisionStatus]);

  const revisionSchedules = useRecordStore((store) => {
    return store.getRevisionSchedules(revisionScheduleFieldName);
  });

  useEffect(() => {
    if (revisionSchedules.length > 0 && !state.finishInitSetting) {
      const gameSetIds = new Set<string>();
      const gameCollectionIds = new Set<string>();
      const gameIds = new Set<string>();
      const fetchGameIds: {
        gameCollectionId: string;
        gameId: string;
        hash: string;
      }[] = [];

      revisionSchedules.map((revisionSchedule) => {
        gameSetIds.add(revisionSchedule.gameSetId);
        gameCollectionIds.add(revisionSchedule.gameCollectionId);
        if (!gameIds.has(revisionSchedule.gameId)) {
          gameIds.add(revisionSchedule.gameId);
          fetchGameIds.push({
            gameCollectionId: revisionSchedule.gameCollectionId,
            gameId: revisionSchedule.gameId,
            hash: revisionSchedule.gameHash,
          });
        }
      });

      setState((s) => ({
        ...s,
        gameSetIds: [...gameSetIds],
        gameCollectionIds: [...gameCollectionIds],
        gameIds: [...gameIds],
        gameIdDetails: [...fetchGameIds],
        finishInitSetting: true,
      }));
    }
  }, [revisionSchedules, state.finishInitSetting]);

  // const findNthStationIndex = (
  //   stations: Station[] | undefined,
  //   number: number,
  // ) => {
  //   let count = 0;

  //   if (stations && number) {
  //     for (let i = 0; i < stations.length; i++) {
  //       if (stations[i]?.type === StationType.Game) {
  //         count++;
  //         if (count === number) {
  //           return i; // Return the index of the nth occurrence of the type
  //         }
  //       }
  //     }
  //   }

  //   return -1; // Return -1 if the nth occurrence is not found
  // };

  const onClickGameInfo = useCallback(
    (type: GameTypeHelpType) => {
      showPopup(<GameTypeHelpPopup type={type} onPrimary={hidePopup} />);
    },
    [hidePopup, showPopup],
  );

  const convertRevisionScheduleToCell = useCallback(
    (
      revisionSchedule: RevisionSchedule | undefined,
      aIRevisionStatus: AIRevisionStatus,
    ) => {
      if (revisionSchedule && userProfile) {
        const classroomMembership = useSpaceStore
          .getState()
          .getSpaceMembership(revisionSchedule.classroomId, userProfile.userId);
        // const album = useSpaceStore
        //   .getState()
        //   .getAlbum(revisionSchedule.gameSetId);
        const gameCollection = useGameStore
          .getState()
          .getGameCollection(revisionSchedule.gameCollectionId);

        const game = games?.find((g) => g?.gameId === revisionSchedule.gameId);
        const gameStatistics = useGameStore
          .getState()
          .getGameStatistics(revisionSchedule.gameId);
        const gameRecords = useRecordStore
          .getState()
          .getGameRecords(gameRecordFieldName);

        // const stationStyle = album?.content?.path[0]?.style;
        // const stations = album?.content?.path.flatMap((path) => path.stations);

        const gameItems = gameCollection?.getPartItemArray((item) => item.game);
        // const _itemIndex = gameItems?.findIndex(
        //   (item) => item.game?.gameId === revisionSchedule.gameId,
        // );
        // const itemIndex = _itemIndex && _itemIndex > -1 ? _itemIndex : 0;
        // const stationIndex = findNthStationIndex(stations, itemIndex + 1);

        // const station = stations?.[stationIndex];
        // const name =
        //   station?.i18nStationInfo?.resources[
        //     station?.i18nStationInfo?.defaultLocale
        //   ]?.name;
        const gameItem = gameItems?.find(
          (item) => item.game?.gameId === revisionSchedule.gameId,
        );
        const lastGameRecord =
          aIRevisionStatus === AIRevisionStatus.Ready
            ? undefined
            : gameRecords
                .filter(
                  (record) =>
                    record.gameId === revisionSchedule.gameId &&
                    record.isNormal,
                )
                .find((record) => record.completed);
        const remainingReward = computeReward(
          classroomMembership?.isAtLeastMember,
          gameItem?.gameReward ?? {},
          false,
          5,
          gameRecords?.map((record) => record.reward) ?? [],
          1,
          gameItem?.game?.answerSlots,
        ).reward;

        if (game) {
          const stationCell: StationCellProps = {
            type: StationCellType.Game,
            props: {
              game: game,
              stationId: undefined, // stationStyle?.stations.game ?? StationGameItemNo.s1,
              name: '',
              index: -1,
              iconKey: gameItem?.icon?.tag ?? '',
              playCount:
                aIRevisionStatus === AIRevisionStatus.Ready
                  ? undefined
                  : gameStatistics?.playCount ?? 0,
              averageScore:
                aIRevisionStatus === AIRevisionStatus.Ready
                  ? undefined
                  : gameStatistics?.averageScore ?? 0,
              userProfile: userProfile,
              remainingBreadReward: remainingReward.joyPoint,
              recordScore: lastGameRecord?.score,
              recordDuration: lastGameRecord?.durationInMs
                ? getTimeString(
                    lastGameRecord?.durationInMs,
                    'HH:mm:ss.SS',
                    true,
                  )
                : undefined,
              isUnlocked:
                aIRevisionStatus === AIRevisionStatus.Scheduled ? false : true,
              aiRevisionStatus: aIRevisionStatus,
              onClickPlay: () => {
                if (aIRevisionStatus !== AIRevisionStatus.Scheduled) {
                  navigate(
                    `/club/${revisionSchedule.classroomId}/journey/${revisionSchedule.gameSetId}/game/${revisionSchedule.gameId}`,
                  );
                } else {
                  toast(
                    t("The scheduled revision isn't avaliable yet."),
                    AppToastInfoOption,
                  );
                }
              },
              onClickInfo: () => {
                const popupInfoType = stringToGameTypeHelpType(
                  gameItem?.icon?.tag,
                );
                if (popupInfoType) {
                  onClickGameInfo(popupInfoType);
                }
              },
            },
          };
          return stationCell;
        }
      }
      return undefined;
    },
    [gameRecordFieldName, games, navigate, onClickGameInfo, t, userProfile],
  );

  const revisionScheduleItems = useMemo(() => {
    const items = revisionSchedules.map((revisionSchedule) => {
      if (state.aiRevisionStatus) {
        const item = convertRevisionScheduleToCell(
          revisionSchedule,
          state.aiRevisionStatus,
        );
        return item;
      }
      return undefined;
    });
    return items.filter((item) => item) as StationCellProps[];
  }, [
    convertRevisionScheduleToCell,
    revisionSchedules,
    state.aiRevisionStatus,
  ]);

  const onClickBoostGame = useCallback(async () => {
    if (classroomId) {
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal + 1 }));
      const revisionGame = await useRecordStore
        .getState()
        .getRevisionScheduleGame({ classroomId, gameSetId });
      setState((s) => ({ ...s, loadingTotal: s.loadingTotal - 1 }));
      if (revisionGame) {
        navigate(
          `/club/${revisionGame.classroomId}/journey/${revisionGame.gameSetId}/game/${revisionGame.gameId}`,
        );
      }
    }
  }, [classroomId, gameSetId, navigate]);

  return (
    <>
      {state.loadingTotal ? (
        <LoadingSpinner loading={!!state.loadingTotal} />
      ) : null}
      {state.aiRevisionStatus && (
        <AIRevisionGamesListTemplate
          type={state.aiRevisionStatus}
          items={revisionScheduleItems}
          onClickBoostAll={() => {
            onClickBoostGame().catch(console.error);
          }}
        />
      )}
    </>
  );
};

export default AIMemoryBoostGameListScreen;
